import { useState, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterLinesIcon, Stars02Icon, Building07Icon, XCloseIcon } from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from '@/components/application/side-panel'
import { FormInput } from '@/components/forms/FormInput'
import { FormSelect } from '@/components/forms/FormSelect'
import { FormDatePicker } from '@/components/forms/FormDatePicker'
import { FormCombobox } from '@/components/forms/FormCombobox'
import { AsyncMultiSelect, type SelectOption } from '@/components/forms/AsyncMultiSelect'
import { useForm, FormProvider } from 'react-hook-form'
import { usePermissions, useAssistantsByIds, useCustomersByIds } from '@/hooks/api'
import type { ConversationChannel, ConversationListFilters, OutcomeType } from '@/types/conversation'
import { basicTags, adminTags } from '@/types/conversation'

interface ConversationFiltersProps {
  filters: ConversationListFilters
  onFiltersChange: (filters: ConversationListFilters) => void
  /** Called before clearing filters - use to clear persisted storage */
  onClear?: () => void
}

// Outcome options will be translated in the component

interface FilterFormValues {
  assistant_id: number[]
  customer_id: number[]
  channel: string
  outcome: string
  tags: string[]
  booking_system: string[]
  topic: string
  summary: string
  from_id: string
  date_from: string
  date_to: string
}

export function ConversationFilters({ filters, onFiltersChange, onClear }: ConversationFiltersProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const { can, isAdmin } = usePermissions()

  // Translated outcome options
  const channelOptions = [
    { value: '', label: t('resources.conversations.filters.channel_all') },
    { value: 'phone', label: t('resources.conversations.filters.channel_phone') },
    { value: 'whatsapp', label: t('resources.conversations.filters.channel_whatsapp') },
  ]

  const outcomeOptions = [
    { value: '', label: t('resources.conversations.outcomes.all') },
    { value: 'completed', label: t('resources.conversations.outcomes.completed') },
    { value: 'caller_follow_up', label: t('resources.conversations.outcomes.caller_follow_up') },
    { value: 'business_follow_up', label: t('resources.conversations.outcomes.business_follow_up') },
    { value: 'transferred', label: t('resources.conversations.outcomes.transferred') },
    { value: 'information_provided', label: t('resources.conversations.outcomes.information_provided') },
    { value: 'no_action', label: t('resources.conversations.outcomes.no_action') },
  ]

  // State for multi-select filters
  const [selectedAssistants, setSelectedAssistants] = useState<SelectOption[]>([])
  const [selectedCustomers, setSelectedCustomers] = useState<SelectOption[]>([])

  // Convert tags to combobox options based on user permissions
  // Admin/partner users see all tags, regular users only see basic tags
  const isAdminOrPartner = can('view:customers') // Using same permission check as customer filter
  const tagOptions = useMemo(() => {
    const availableTags = isAdminOrPartner
      ? { ...basicTags, ...adminTags }
      : basicTags
    return Object.entries(availableTags).map(([key, label]) => ({
      value: key,
      label,
    }))
  }, [isAdminOrPartner])

  const bookingSystemOptions = useMemo(() => [
    { value: 'formitable', label: 'Formitable' },
    { value: 'opentable', label: 'OpenTable' },
    { value: 'fork', label: 'TheFork' },
    { value: 'guestplan', label: 'Guestplan' },
    { value: 'zenchef', label: 'Zenchef' },
    { value: 'gotable', label: 'GoTable' },
    { value: 'tablefever', label: 'TableFever' },
  ], [])

  const methods = useForm<FilterFormValues>({
    defaultValues: {
      assistant_id: filters.assistant_id || [],
      customer_id: filters.customer_id || [],
      channel: filters.channel || '',
      outcome: filters.outcome || '',
      tags: filters.tags || [],
      booking_system: filters.booking_system || [],
      topic: filters.topic || '',
      summary: filters.summary || '',
      from_id: filters.from_id || '',
      date_from: filters.date_from || '',
      date_to: filters.date_to || '',
    },
  })

  // Fetch selected assistants reactively when filter IDs exist
  const { data: existingAssistants = [] } = useAssistantsByIds(
    filters.assistant_id || [],
    (filters.assistant_id?.length || 0) > 0
  )

  // Create a stable key based on assistant IDs for comparison
  const existingAssistantIds = existingAssistants.map((a) => a.id).join(',')
  const filterAssistantIds = (filters.assistant_id || []).join(',')

  // Initialize selected assistants from fetched data
  useEffect(() => {
    if (existingAssistants.length > 0) {
      setSelectedAssistants(existingAssistants.map((a) => ({ id: a.id, name: a.name })))
    } else if (!filters.assistant_id?.length) {
      setSelectedAssistants([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAssistantIds, filterAssistantIds])

  // Fetch selected customers reactively when filter IDs exist
  const { data: existingCustomers = [] } = useCustomersByIds(
    filters.customer_id || [],
    (filters.customer_id?.length || 0) > 0 && can('view:customers')
  )

  // Create stable keys for comparison to avoid infinite loops
  const existingCustomerIds = existingCustomers.map((c) => c.id).join(',')
  const filterCustomerIds = (filters.customer_id || []).join(',')

  // Initialize selected customers from fetched data
  useEffect(() => {
    if (existingCustomers.length > 0) {
      setSelectedCustomers(existingCustomers.map((c) => ({ id: c.id, name: c.name })))
    } else if (!filters.customer_id?.length) {
      setSelectedCustomers([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCustomerIds, filterCustomerIds])

  const handleApply = useCallback(
    (values: FilterFormValues) => {
      const newFilters: ConversationListFilters = {}

      // Use selected items from state, not form values
      if (selectedAssistants.length) {
        newFilters.assistant_id = selectedAssistants.map((a) => a.id)
      }
      if (selectedCustomers.length) {
        newFilters.customer_id = selectedCustomers.map((c) => c.id)
      }
      if (values.channel) {
        newFilters.channel = values.channel as ConversationChannel
      }
      if (values.outcome) {
        newFilters.outcome = values.outcome as OutcomeType
      }
      if (values.tags?.length) {
        newFilters.tags = values.tags
      }
      if (values.booking_system?.length) {
        newFilters.booking_system = values.booking_system
      }
      if (values.topic) {
        newFilters.topic = values.topic
      }
      if (values.summary) {
        newFilters.summary = values.summary
      }
      if (values.from_id) {
        newFilters.from_id = values.from_id
      }
      if (values.date_from) {
        newFilters.date_from = values.date_from
      }
      if (values.date_to) {
        newFilters.date_to = values.date_to
      }

      onFiltersChange(newFilters)
      setIsOpen(false)
    },
    [onFiltersChange, selectedAssistants, selectedCustomers]
  )

  const handleClear = useCallback(() => {
    methods.reset({
      assistant_id: [],
      customer_id: [],
      channel: '',
      outcome: '',
      tags: [],
      booking_system: [],
      topic: '',
      summary: '',
      from_id: '',
      date_from: '',
      date_to: '',
    })
    setSelectedAssistants([])
    setSelectedCustomers([])
    // Clear persisted storage first to prevent restoration
    onClear?.()
    onFiltersChange({})
    setIsOpen(false)
  }, [methods, onFiltersChange, onClear])

  // Quick clear without opening panel
  const handleQuickClear = useCallback(() => {
    methods.reset({
      assistant_id: [],
      customer_id: [],
      channel: '',
      outcome: '',
      tags: [],
      booking_system: [],
      topic: '',
      summary: '',
      from_id: '',
      date_from: '',
      date_to: '',
    })
    setSelectedAssistants([])
    setSelectedCustomers([])
    // Clear persisted storage first to prevent restoration
    onClear?.()
    onFiltersChange({})
  }, [methods, onFiltersChange, onClear])

  const activeFilterCount = Object.keys(filters).filter(
    (key) => {
      const value = filters[key as keyof ConversationListFilters]
      return value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0)
    }
  ).length

  return (
    <>
      <div className="inline-flex items-center overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xs dark:border-gray-600 dark:bg-gray-800">
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <FilterLinesIcon className="h-4 w-4" />
          {t('resources.conversations.filters.title')}
          {activeFilterCount > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-500 px-1.5 text-xs font-medium text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />
            <button
              onClick={handleQuickClear}
              className="flex items-center justify-center px-2 py-1.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              title={t('resources.generic.clear_all')}
            >
              <XCloseIcon className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      <SidePanel
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        onClose={() => setIsOpen(false)}
        side="right"
        size="md"
        variant="filter"
      >
        <SidePanelContent>
          <SidePanelHeader
            title={t('resources.conversations.filters.title')}
            onClose={() => setIsOpen(false)}
          />
          <SidePanelBody>
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(handleApply)}
                className="space-y-6"
              >
                {/* Assistant filter */}
                <AsyncMultiSelect
                  label={t('resources.conversations.filters.assistant')}
                  placeholder={t('resources.conversations.filters.search_assistants')}
                  searchPlaceholder={t('resources.generic.search')}
                  searchEndpoint="/assistants"
                  value={selectedAssistants}
                  onChange={setSelectedAssistants}
                  emptyMessage={t('resources.dashboard.filters.no_results')}
                  icon={Stars02Icon}
                />

                {/* Customer filter (partner/admin only) */}
                {can('view:customers') && (
                  <AsyncMultiSelect
                    label={t('resources.conversations.filters.customer')}
                    placeholder={t('resources.conversations.filters.search_customers')}
                    searchPlaceholder={t('resources.generic.search')}
                    searchEndpoint="/customers"
                    value={selectedCustomers}
                    onChange={setSelectedCustomers}
                    emptyMessage={t('resources.dashboard.filters.no_results')}
                    icon={Building07Icon}
                  />
                )}

                {/* Channel filter */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('resources.conversations.filters.channel')}
                  </label>
                  <FormSelect
                    name="channel"
                    control={methods.control}
                    options={channelOptions}
                  />
                </div>

                {/* Outcome filter */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('resources.conversations.filters.outcome')}
                  </label>
                  <FormSelect
                    name="outcome"
                    control={methods.control}
                    options={outcomeOptions}
                  />
                </div>

                {/* Tags filter */}
                <div>
                  <FormCombobox
                    name="tags"
                    control={methods.control}
                    label={t('resources.conversations.filters.tags')}
                    placeholder={t('resources.conversations.filters.search_tags')}
                    options={tagOptions}
                    multiple
                  />
                </div>

                {/* Booking system filter (admin only) */}
                {isAdmin && (
                  <div>
                    <FormCombobox
                      name="booking_system"
                      control={methods.control}
                      label={t('resources.conversations.filters.booking_system')}
                      placeholder={t('resources.conversations.filters.search_booking_system')}
                      options={bookingSystemOptions}
                      multiple
                    />
                  </div>
                )}

                {/* Content search */}
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('resources.conversations.filters.topic')}
                    </label>
                    <FormInput name="topic" control={methods.control} placeholder={t('resources.conversations.filters.search_topic')} />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('resources.conversations.filters.summary')}
                    </label>
                    <FormInput name="summary" control={methods.control} placeholder={t('resources.conversations.filters.search_summary')} />
                  </div>
                </div>

                {/* Contact search */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('resources.conversations.filters.phone_number')}
                  </label>
                  <FormInput name="from_id" control={methods.control} placeholder={t('resources.conversations.filters.search_phone')} />
                </div>

                {/* Date range */}
                <div className="grid grid-cols-2 gap-4">
                  <FormDatePicker
                    name="date_from"
                    control={methods.control}
                    label={t('resources.conversations.filters.from_date')}
                  />
                  <FormDatePicker
                    name="date_to"
                    control={methods.control}
                    label={t('resources.conversations.filters.to_date')}
                  />
                </div>
              </form>
            </FormProvider>
          </SidePanelBody>
          <SidePanelFooter>
            <Button color="secondary" onPress={handleClear}>
              {t('resources.generic.clear_all')}
            </Button>
            <Button color="primary" onPress={methods.handleSubmit(handleApply)}>
              {t('resources.generic.apply_filters')}
            </Button>
          </SidePanelFooter>
        </SidePanelContent>
      </SidePanel>
    </>
  )
}

export default ConversationFilters
